import { ApiResponse } from '../types';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TIMEOUT_MS = 100000;

export interface FetchResponse<T = any> {
  data: T;
  status: number;
  headers: Headers;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const azureToken = localStorage.getItem('azureAuthToken');
  const jwtToken = localStorage.getItem('authToken');
  const token = azureToken || jwtToken;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request<T = any>(
  method: string,
  url: string,
  data?: any,
  config?: any
): Promise<FetchResponse<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const headers: Record<string, string> = {
    ...getAuthHeaders(),
  };

  const isFormData = data instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    signal: controller.signal,
  };

  if (data !== undefined) {
    fetchOptions.body = isFormData ? data : JSON.stringify(data);
  }

  try {
    const response = await fetch(`${BASE_URL}${url}`, fetchOptions);
    clearTimeout(timer);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('azureAuthToken');
      window.location.href = '/login';
      return Promise.reject(
        Object.assign(new Error('Unauthorized'), {
          response: { status: 401 },
        })
      );
    }

    if (!response.ok) {
      const error = Object.assign(new Error(`Request failed with status ${response.status}`), {
        response: { status: response.status, data: null },
      });
      throw error;
    }

    let responseData: any;
    if (config?.responseType === 'blob') {
      responseData = await response.blob();
    } else {
      const text = await response.text();
      try {
        responseData = JSON.parse(text);
      } catch {
        responseData = text;
      }
    }

    return {
      data: responseData as T,
      status: response.status,
      headers: response.headers,
    };
  } catch (err: any) {
    clearTimeout(timer);
    if (err.name === 'AbortError') {
      throw Object.assign(new Error('Request timeout'), {
        response: { status: 0 },
      });
    }
    if (!err.response) {
      throw Object.assign(err, {
        response: { status: 0 },
      });
    }
    throw err;
  }
}

export const apiClient = {
  get: async <T = any>(url: string, config?: any): Promise<FetchResponse<T>> => {
    return request<T>('GET', url, undefined, config);
  },

  post: async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    const response = await request<T>('POST', url, data);
    return response.data as unknown as ApiResponse<T>;
  },

  put: async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    const response = await request<T>('PUT', url, data);
    return response.data as unknown as ApiResponse<T>;
  },

  delete: async <T = any>(url: string): Promise<ApiResponse<T>> => {
    const response = await request<T>('DELETE', url);
    return response.data as unknown as ApiResponse<T>;
  },

  patch: async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    const response = await request<T>('PATCH', url, data);
    return response.data as unknown as ApiResponse<T>;
  },
};

const api = {
  get: <T = any>(url: string, config?: any): Promise<FetchResponse<T>> => {
    return request<T>('GET', url, undefined, config);
  },
};

export default api;
